import { formatDonorApiError } from '../donorFormUtils';
import { sdk } from '../sdk';
import { getErrorMessage } from '../utils/errorMessage';

export interface Donor {
  _id: string;
  donorId: string; // Changed from number to string (e.g., "D001")
  name: string;
  fatherOrHusbandName: string;
  relation: 'Father' | 'Husband';
  cnic: string;
  phone?: string; // Made optional
  email?: string;
  officeId: string | {
    _id: string;
    name: string;
    officeNo: number;
  };
  isGuest: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DonorsResponse {
  success: boolean;
  data: Donor[];
  message: string;
}

export interface DonorResponse {
  success: boolean;
  data: Donor;
  message: string;
}

export interface CreateDonorData {
  name: string;
  fatherOrHusbandName: string;
  relation: 'Father' | 'Husband';
  cnic: string;
  phone?: string; // Made optional
  email?: string;
  address?: string;
  officeId: string;
}

export interface UpdateDonorData extends Partial<CreateDonorData> {}

export interface ConvertGuestDonorData {
  guestId: string; // Changed from number to string (e.g., "G001")
  cnic: string;
  officeId?: string;
}

export class DonorsAPI {
  // Get all donors
  static async getAllDonors(params?: { page?: number; limit?: number; search?: string; officeId?: string }): Promise<{ data: Donor[]; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.officeId) queryParams.append('officeId', params.officeId);
      
      const url = `/api/v1/donors${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await sdk.client.get(url);
      
      // If response is empty or undefined, return empty array
      if (!response) {
        return { data: [] };
      }
      
      // Check if the response has the expected structure
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donors');
      }
      
      // Return data with pagination info if available
      return {
        data: response.data || [],
        pagination: response.pagination
      };
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(getErrorMessage(error, 'Failed to fetch donors'));
      }
    }
  }

  // Get donor by ID
  static async getDonorById(id: string): Promise<Donor> {
    try {
      const response = await sdk.client.get(`/api/v1/donors/${id}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donor');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Create new donor
  static async createDonor(data: CreateDonorData): Promise<Donor> {
    try {
      const response = await sdk.client.post('/api/v1/donors', data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create donor');
      }
      
      return response.data;
    } catch (error: unknown) {
      throw new Error(formatDonorApiError(error, 'Failed to create donor. Please try again.'));
    }
  }

  // Update donor
  static async updateDonor(id: string, data: UpdateDonorData): Promise<Donor> {
    try {
      const response = await sdk.client.put(`/api/v1/donors/${id}`, data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update donor');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid data provided. Please check your input and try again.');
      } else if (error?.response?.status === 404) {
        throw new Error('Donor not found.');
      } else if (error?.response?.status === 409) {
        throw new Error('A donor with this CNIC already exists.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to update donor. Please try again.'));
      }
    }
  }

  // Delete donor
  static async deleteDonor(id: string): Promise<void> {
    try {
      const response = await sdk.client.delete(`/api/v1/donors/${id}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete donor');
      }
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Cannot delete donor. Donor may have associated donations. Please check and try again.');
      } else if (error?.response?.status === 404) {
        throw new Error('Donor not found.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to delete donor. Please try again.'));
      }
    }
  }

  // Search donors by various criteria
  static async searchDonors(searchTerm: string, officeId?: string): Promise<Donor[]> {
    try {
      const params = new URLSearchParams({ search: searchTerm });
      if (officeId) params.append('officeId', officeId);
      
      const response = await sdk.client.get(`/api/v1/donors/search?${params}`);
      return response.data.data || [];
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to search donors'));
    }
  }

  // Search donor by CNIC
  static async searchDonorByCNIC(cnic: string): Promise<any> {
    try {
      const response = await sdk.client.get(`/api/v1/donors/search-by-cnic/${encodeURIComponent(cnic)}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Could not complete the search. Please try again.'));
    }
  }

  // Search donor by phone
  static async searchDonorByPhone(phone: string): Promise<any> {
    try {
      const response = await sdk.client.get(`/api/v1/donors/search-by-phone/${encodeURIComponent(phone)}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Could not complete the search. Please try again.'));
    }
  }

  // Convert guest donor to permanent donor
  static async convertGuestToDonor(data: ConvertGuestDonorData): Promise<Donor> {
    try {
      const response = await sdk.client.post('/api/v1/donors/convert-guest', data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to convert guest donor');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid data provided. Please check your input and try again.');
      } else if (error?.response?.status === 404) {
        throw new Error('Guest donor not found.');
      } else if (error?.response?.status === 409) {
        throw new Error('A donor with this CNIC already exists.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to convert guest donor. Please try again.'));
      }
    }
  }

  // Get donor statistics
  static async getDonorStats(): Promise<any> {
    try {
      const response = await sdk.client.get('/api/v1/donors/stats');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donor statistics');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(getErrorMessage(error, 'Failed to fetch donor statistics'));
      }
    }
  }
}
